/**
 * Hilfslogik des Dialogs „Beteiligte zuordnen“ (AddParticipantDialog).
 *
 * Kundenfeedback 16.10.2026 Teil 2 (XXV): „Ich würde auf der linken Seite
 * einfach nur anzeigen, wer schon in der Akte in welcher Rolle beteiligt ist,
 * und dann kann ich rechts nach neuen Kontakten für die Akte suchen.“
 *
 * Reines Datenmodell ohne Vue-Abhängigkeit, damit Gruppierung, Statustexte
 * und Rollenfilter testbar bleiben. Alle Funktionen liefern neue Objekte.
 */
import type { Contact } from "@/services/contactModel";
import {
  ALL_CONTACTS_KEY,
  PARTICIPANT_CATEGORIES,
  type CategoryPriority,
  type ParticipantCategory,
  contactMatchesCategory,
  findCategory,
  matchesContactQuery,
  sortContactsForOverview,
} from "../participantCategories";

/** Rollenfilter „Alle Rollen“ (= bisherige Gesamtansicht „Meine Kontakte“). */
export const ALL_ROLES_KEY = ALL_CONTACTS_KEY;
/** Rollenfilter/Gruppe „Kunde“ (Mandant, tbl_cases_clients – keine Fall-Rolle). */
export const MANDANT_ROLE_KEY = "_mandant";
/** Kontakttyp des Adressbuchs, der als Kunde übernommen werden kann. */
const MANDANT_CONTACT_TYPE = "Kunde";
const MANDANT_LABEL = "Kunde";

const PRIORITY_ORDER: Readonly<Record<CategoryPriority, number>> = { pflicht: 0, empfohlen: 1, optional: 2 };

/** Beteiligter aus `GET /protected/cases/{id}/involved` (nur die hier genutzten Felder). */
export interface InCaseParticipant {
  id?: number | string | null;
  /** "mandant" für Kunden; sonst undefined/anderer Wert */
  type?: string | null;
  /** internal | pending | external | unregistered */
  status?: string | null;
  email?: string | null;
  name?: string | null;
  roles?: readonly string[] | null;
  removedAt?: string | null;
  /** Externe: Zeitpunkt der Einladung (POST …/externals/{id}/invite) */
  invitedAt?: string | null;
  /** Beitritt zum Fall (Backend, optional): none | pending | joined */
  participationStatus?: string | null;
  /** Organisation/Firma des Beteiligten (Kanzlei, Werkstatt, SV-Büro …) */
  companyName?: string | null;
  /** Eigene handelnde Kanzlei (Backend-Ergänzung ohne eigenen Datensatz) */
  isSelf?: boolean | null;
}

export interface InCaseMember {
  id: number | string;
  /**
   * Anzeigename: bei Unternehmen die Organisation (z. B. „Polz & Kollegen“),
   * sonst die Person (Kundenfeedback 24.10.2026, XIX).
   */
  name: string;
  /** Ansprechpartner als Zusatz, wenn `name` die Organisation ist */
  person: string;
  /** Status in Klartext: beigetreten / eingeladen / nicht eingeladen / … */
  statusText: string;
  /** Kunden hängen am Fall-Stammsatz und werden im Dialog nicht entfernt. */
  canRemove: boolean;
  participant: InCaseParticipant;
}

export interface InCaseGroup {
  /** MANDANT_ROLE_KEY oder Fall-Rolle (tbl_cases_externals.roles) */
  key: string;
  label: string;
  priority: CategoryPriority;
  members: InCaseMember[];
  /** Zusätzliche Einträge ohne Beteiligten-Datensatz (aus Falldaten erkannte Versicherungen). */
  extraCount: number;
  isEmpty: boolean;
}

/** Status eines Beteiligten in Klartext – ohne Farbe, ohne Icon. */
export function participantStatusText(p: InCaseParticipant): string {
  if (p.participationStatus === "pending") { return "Beitritt angefragt"; }
  const status = p.status ?? "";
  if (status === "internal") { return "beigetreten"; }
  if (status === "pending") { return "eingeladen"; }
  if (status === "unregistered") { return "nicht eingeladen"; }
  if (status === "external") {
    if (!(p.email ?? "").trim()) { return "ohne E-Mail"; }
    // Externe bleiben nach der Einladung „external“; das Backend setzt invitedAt.
    return p.invitedAt ? "eingeladen" : "nicht eingeladen";
  }
  return "";
}

/** Externe heißen im Backend „Name <adresse>“ – die Adresse steht links nur im Tooltip. */
export function displayNameWithoutEmail(name: string | null | undefined): string {
  return (name ?? "").replace(/\s*<[^>]*>\s*$/, "").trim();
}

/**
 * Kundenfeedback 24.10.2026 (XIX): „Bei Rechtsanwaltskanzlei steht Franziska
 * Grotz – hier müsste Polz und Kollegen stehen.“ Rollen, die Unternehmen
 * sind, zeigen die Organisation; die Person steht als Zusatz daneben.
 * Kunden behalten ihren Namen (das Backend liefert dort bereits Person oder
 * Firma).
 */
export function participantDisplayParts(p: InCaseParticipant, isMandant = false): { name: string; person: string } {
  const person = displayNameWithoutEmail(p.name) || (p.email ?? "").trim();
  const company = isMandant ? "" : (p.companyName ?? "").trim();
  if (company && company.toLowerCase() !== person.toLowerCase()) {
    return { name: company, person };
  }
  return { name: person || company || "Unbenannt", person: "" };
}

function toMember(p: InCaseParticipant, isMandant: boolean): InCaseMember {
  const { name, person } = participantDisplayParts(p, isMandant);
  return {
    id: p.id ?? "",
    name,
    person,
    statusText: participantStatusText(p),
    // id 0 = eigene Kanzlei ohne Datensatz (Backend-Ergänzung) – nicht entfernbar.
    canRemove: !isMandant && p.id != null && p.id !== 0 && p.id !== "",
    participant: p,
  };
}

function isActive(p: InCaseParticipant): boolean {
  return !p.removedAt;
}

function hasKnownRole(p: InCaseParticipant, categories: readonly ParticipantCategory[]): boolean {
  const roles = p.roles ?? [];
  return categories.some((c) => !c.catchAll && roles.includes(c.role));
}

function membersForCategory(
  cat: ParticipantCategory,
  externals: readonly InCaseParticipant[],
  categories: readonly ParticipantCategory[],
): InCaseMember[] {
  return externals
    .filter((p) => (cat.catchAll
      ? (p.roles ?? []).includes(cat.role) || !hasKnownRole(p, categories)
      : (p.roles ?? []).includes(cat.role)))
    .map((p) => toMember(p, false));
}

export interface BuildInCaseGroupsOptions {
  /** Aus Falldaten erkannte Versicherungen (zählen als besetzt). */
  autoInsuranceCount?: number;
  categories?: readonly ParticipantCategory[];
}

/**
 * Gruppiert die aktiven Beteiligten nach Rolle für die linke Spalte
 * „In dieser Akte“: Kunde zuerst, dann besetzte Rollen in Kategorie-
 * Reihenfolge, zuletzt leere Rollen („Noch niemand“) – Pflicht vor
 * Empfohlen vor Optional.
 */
export function buildInCaseGroups(
  participants: readonly InCaseParticipant[],
  options: BuildInCaseGroupsOptions = {},
): InCaseGroup[] {
  const categories = options.categories ?? PARTICIPANT_CATEGORIES;
  const autoInsuranceCount = options.autoInsuranceCount ?? 0;
  const active = participants.filter(isActive);
  const mandanten = active.filter((p) => p.type === "mandant");
  const externals = active.filter((p) => p.type !== "mandant");

  const kunde: InCaseGroup = {
    key: MANDANT_ROLE_KEY,
    label: MANDANT_LABEL,
    priority: "pflicht",
    members: mandanten.map((p) => toMember(p, true)),
    extraCount: 0,
    isEmpty: mandanten.length === 0,
  };

  const roleGroups: InCaseGroup[] = categories.map((cat) => {
    const members = membersForCategory(cat, externals, categories);
    const extraCount = cat.role === "Versicherung" ? autoInsuranceCount : 0;
    return {
      key: cat.role,
      label: cat.label,
      priority: cat.priority,
      members,
      extraCount,
      isEmpty: members.length === 0 && extraCount === 0,
    };
  });

  const filled = roleGroups.filter((g) => !g.isEmpty);
  const empty = roleGroups
    .map((g, idx) => ({ g, idx }))
    .filter(({ g }) => g.isEmpty)
    .sort((a, b) => (PRIORITY_ORDER[a.g.priority] - PRIORITY_ORDER[b.g.priority]) || (a.idx - b.idx))
    .map(({ g }) => g);

  return [kunde, ...filled, ...empty];
}

export interface RoleFilterOption {
  value: string;
  label: string;
}

/** Einträge des Rollen-Dropdowns der Suche: Alle Rollen, Kunde, dann jede Kategorie. */
export function roleFilterOptions(categories: readonly ParticipantCategory[] = PARTICIPANT_CATEGORIES): RoleFilterOption[] {
  return [
    { value: ALL_ROLES_KEY, label: "Alle Rollen" },
    { value: MANDANT_ROLE_KEY, label: MANDANT_LABEL },
    ...categories.map((c) => ({ value: c.role, label: c.label })),
  ];
}

/** Anzeigename eines Rollenfilters (für Überschriften und Knöpfe). */
export function roleFilterLabel(roleKey: string): string {
  if (roleKey === MANDANT_ROLE_KEY) { return MANDANT_LABEL; }
  if (roleKey === ALL_ROLES_KEY) { return ""; }
  return findCategory(roleKey)?.label ?? roleKey;
}

/**
 * Eigene Kontakte für Rollenfilter + Suchbegriff. Kunde = Kontakte vom Typ
 * „Kunde“; „Alle Rollen“ = gesamtes Adressbuch. Favoriten zuerst, dann A–Z.
 */
export function filterContactsForRole<T extends Contact>(contacts: readonly T[], roleKey: string, query: string): T[] {
  const byRole = contacts.filter((c) => {
    if (roleKey === ALL_ROLES_KEY) { return true; }
    if (roleKey === MANDANT_ROLE_KEY) { return (c.contactType ?? "") === MANDANT_CONTACT_TYPE; }
    const cat = findCategory(roleKey);
    return cat ? contactMatchesCategory(c, cat) : false;
  });
  return sortContactsForOverview(byRole.filter((c) => matchesContactQuery(c, query)));
}

/** Frisch angelegter Beteiligter (Ergebnis von „Hinzufügen“). */
export interface CreatedParticipantLike {
  idParticipant: number;
  selection: { type: string; email?: string | null; name?: string | null; role?: string | null };
}

export interface InvitableParticipant {
  id: number;
  name: string;
  email: string;
}

/**
 * Wer kann nach „Hinzufügen“ direkt zur Akte eingeladen werden? Nur externe
 * Beteiligte mit E-Mail (Kontakt oder Zentrale) – Versicherungen werden nicht
 * eingeladen, Portal-Abteilungen sind bereits registriert.
 */
export function invitableFromCreated(created: readonly CreatedParticipantLike[]): InvitableParticipant[] {
  return created
    .filter((c) => Number.isFinite(c.idParticipant) && c.idParticipant > 0)
    .filter((c) => c.selection.type === "contact" || c.selection.type === "central")
    .filter((c) => c.selection.role !== "Versicherung" && c.selection.role !== "Versicherungsmakler")
    .filter((c) => !!(c.selection.email ?? "").trim())
    .map((c) => ({
      id: c.idParticipant,
      name: (c.selection.name ?? "").trim() || (c.selection.email ?? "").trim(),
      email: (c.selection.email ?? "").trim(),
    }));
}
